import { useFlags } from 'launchdarkly-react-client-sdk';
import { Route, Routes } from 'react-router-dom';

import { FreshchatDisplay } from './FreshchatDisplay';
import { SegmentPageTracker } from './SegmentPageTracker';
import { NavigationSync } from 'features/communication-bridge/NavigationSync';
import { TitleSync } from 'features/communication-bridge/TitleSync';

import {
	WithLoggedInComponent,
	WithSubscriptionRules
} from 'components/RouteComponents/RouteComponents';
import { Scroller } from 'components/UI/Scroller';
import { ImportCodelistPage } from 'features/codelist/import/ImportCodelistPage';
import { CreateEntryPageV1_5 } from 'features/entry-form-v2/create-entry/CreateEntryPageV1_5';
import { PrintEntryPage } from 'features/entry-form-v2/print/PrintEntryPage';
import { CreateSeriesEntryPageV1_5 } from 'features/entry-form-v2/series/create/CreateSeriesEntryPageV1_5';
import { SeriesDetailsPage } from 'features/entry-form-v2/series/details/SeriesDetailsPage';
import { UpdateSeriesEntryPageV1_5 } from 'features/entry-form-v2/series/update/UpdateSeriesEntryPageV1_5';
import {
	SurveyCompletedPage,
	SurveyExpiredPinPage,
	SurveyFormPage,
	SurveyInvalidPinPage,
	SurveyLayout,
	SurveyMaxAttemptsExceededPage,
	SurveyPinEntryPage,
	SurveySuccessPage
} from 'features/entry-form-v2/survey';
import { UpdateEntryPageV1_5 } from 'features/entry-form-v2/update-entry/UpdateEntryPageV1_5';
import { ROUTE_MAP } from 'features/entry-form-v2/utils/routeMap';
import InviteUsersPage from 'features/invite-users/InviteUsersPage';
import { stringAsBoolean } from 'helpers/generic';
import { StorePrevLocation } from 'helpers/storePrevLocation';
import {
	AccountUMBillingPage,
	AccountUMPaymentPage,
	AccountUMSubscriptionPage
} from 'pages/Account';
import {
	AdminEnterprisePage,
	AdminUserCreatePage,
	AdminUserDetailsPage,
	AdminUsersPage
} from 'pages/Admin';
import { AnalysisPage } from 'pages/Analysis';
import { CollaboratorsInvitePage, CollaboratorsPage } from 'pages/Collaborators';
import { AddEditEntryPage, DatasetPage } from 'pages/Dataset';
import { DependenciesPage } from 'pages/Dependencies';
import { DocumentsPage } from 'pages/Documents';
import {
	EnterpriseAdminProjectsPage,
	EnterpriseAdminSettingsPage,
	EnterpriseAdminSubscriptionPage
} from 'pages/EnterpriseAdmin';
import EnterpriseAdminProjectApproval from 'pages/EnterpriseAdmin/EnterpriseAdminProjectApprovalPage';
import { FormDesignerPage, FormsPage } from 'pages/Forms';
import { HistoryPage } from 'pages/History';
import { NotFoundPage } from 'pages/NotFound';
import { CreateAndImportPage, ProjectsPage } from 'pages/Projects';
import { RolesPage } from 'pages/Roles';
import { StatusesPage } from 'pages/Statuses';
import { TemplatesPage } from 'pages/Templates';
import { VariablesPage } from 'pages/Variables';
import { ROUTES } from 'types/navigation';
import { NavigationHub } from './NavigationHub';
import { SurveyNoVariablesPage } from 'features/entry-form-v2/survey/pages/SurveyNoVariables';
import { SurveyNoFormPage } from 'features/entry-form-v2/survey/pages/SurveyNoForm';
import { UnauthorizedPage } from 'pages/Unauthorized';

const ADMIN_PATH_ROOT = `/${process.env.REACT_APP_ADMIN_PATH_ROOT}`;
const ENTERPRISE_ADMIN_PATH_ROOT = `/${process.env.REACT_APP_ENTERPRISE_ADMIN_PATH_ROOT}`;

const IS_FRESHCHAT_ENABLED = stringAsBoolean(process.env.REACT_APP_USE_FRESHCHAT!);
const IS_FORMS_DESGINER_PAGE_ENABLED = stringAsBoolean(process.env.REACT_APP_USE_FORMS_DESIGNER!);
const IS_TEMPLATES_PAGE_ENABLED = stringAsBoolean(process.env.REACT_APP_USE_TEMPLATES!);
const IS_SEGMENT_TRACKING_ENABLED = stringAsBoolean(process.env.REACT_APP_USE_SEGMENT_TRACKING!);
const ARE_ENTERPRISE_ADMIN_ROUTES_ACTIVATED = stringAsBoolean(
	process.env.REACT_APP_USE_ENTERPRISE_ADMIN_ROUTES!
);

export function Navigation() {
	const {
		auditLogV1,
		usingNewInvitationFlow,
		teamCoreTextTemplateEnabled,
		teamCoreRegistriesEnabled
	} = useFlags();

	return (
		<>
			<NavigationSync />
			<TitleSync />
			<Scroller />
			<NavigationHub />
			<StorePrevLocation />

			{IS_FRESHCHAT_ENABLED && <FreshchatDisplay />}
			{IS_SEGMENT_TRACKING_ENABLED && <SegmentPageTracker />}

			<Routes>
				{/* ── Public survey routes (no auth required) ──────────── */}
				<Route path={ROUTE_MAP.surveys.byId.path} element={<SurveyLayout />}>
					<Route index element={<SurveyPinEntryPage />} />
					<Route path={ROUTE_MAP.surveys.byId.form.path} element={<SurveyFormPage />} />
					<Route
						path={ROUTE_MAP.surveys.byId.invalidPin.path}
						element={<SurveyInvalidPinPage />}
					/>
					<Route
						path={ROUTE_MAP.surveys.byId.expired.path}
						element={<SurveyExpiredPinPage />}
					/>
					<Route
						path={ROUTE_MAP.surveys.byId.maxAttemptsExceeded.path}
						element={<SurveyMaxAttemptsExceededPage />}
					/>
					<Route
						path={ROUTE_MAP.surveys.byId.completed.path}
						element={<SurveyCompletedPage />}
					/>
					<Route
						path={ROUTE_MAP.surveys.byId.success.path}
						element={<SurveySuccessPage />}
					/>
					<Route
						path={ROUTE_MAP.surveys.byId.noVariables.path}
						element={<SurveyNoVariablesPage />}
					/>
					<Route
						path={ROUTE_MAP.surveys.byId.noForm.path}
						element={<SurveyNoFormPage />}
					/>
				</Route>

				{/* ── Authenticated app routes (auth handled by shell) ── */}
				<Route element={<WithLoggedInComponent />}>
					<>
						<Route
							path={ROUTE_MAP.projects.byId.dataset.update.path}
							element={<UpdateEntryPageV1_5 />}
						/>
						<Route
							path={ROUTE_MAP.projects.byId.dataset.update.print.path}
							element={<PrintEntryPage />}
						/>
					</>

					<>
						<Route
							path={ROUTE_MAP.projects.byId.dataset.create.path}
							element={<CreateEntryPageV1_5 />}
						/>
						<Route
							path={ROUTE_MAP.projects.byId.dataset.create.print.path}
							element={<PrintEntryPage />}
						/>
					</>

					<>
						<Route
							path={
								ROUTE_MAP.projects.byId.dataset.update.series.bySeriesName.create
									.path
							}
							element={<CreateSeriesEntryPageV1_5 />}
						/>
						<Route
							path={
								ROUTE_MAP.projects.byId.dataset.update.series.bySeriesName.create
									.print.path
							}
							element={<PrintEntryPage />}
						/>
					</>

					<>
						<Route
							path={ROUTE_MAP.projects.byId.dataset.update.series.bySeriesName.path}
							element={<SeriesDetailsPage />}
						/>
						<Route
							path={
								ROUTE_MAP.projects.byId.dataset.update.series.bySeriesName.update
									.print.path
							}
							element={<PrintEntryPage />}
						/>
					</>

					<Route
						path={
							ROUTE_MAP.projects.byId.dataset.update.series.bySeriesName.update.path
						}
						element={<UpdateSeriesEntryPageV1_5 />}
					/>

					<Route
						path={ROUTE_MAP.projects.byId.dataset.codelist.import.path}
						element={<ImportCodelistPage />}
					/>

					<Route path={ROUTES.Analysis} element={<AnalysisPage />} />
					<Route path={ROUTES.Dataset} element={<DatasetPage />} />

					<Route path={ROUTES.CreateEntry} element={<AddEditEntryPage />} />
					<Route path={ROUTES.UpdateEntry} element={<AddEditEntryPage />} />
					{auditLogV1 && (
						<Route
							path={ROUTE_MAP.projects.byId.dataset.history.path}
							element={<HistoryPage />}
						/>
					)}
					<Route path={ROUTES.Variables} element={<VariablesPage />} />
					<Route path={ROUTES.CreateVariable} element={<VariablesPage />} />
					<Route path={ROUTES.Documents} element={<DocumentsPage />} />
					<Route path={ROUTES.Dependencies} element={<DependenciesPage />} />
					<Route path={ROUTES.EntryStatuses} element={<StatusesPage />} />
					<Route
						path={ROUTES.AddCollaborators}
						element={
							usingNewInvitationFlow ? (
								<InviteUsersPage />
							) : (
								<CollaboratorsInvitePage />
							)
						}
					/>
					<Route path={ROUTES.Collaborators} element={<CollaboratorsPage />} />
					<Route path={ROUTES.Projects} element={<ProjectsPage />} />
					<Route path={ROUTES.CreateProject} element={<CreateAndImportPage />} />
					<Route path={ROUTES.ImportDataset} element={<CreateAndImportPage />} />
					<Route path={ROUTES.Roles} element={<RolesPage />} />
					{/* ACCOUNT USER MANAGEMENT*/}
					<Route
						path={ROUTES.AccountUMSubscription}
						element={<AccountUMSubscriptionPage />}
					/>
					{/* ADMIN */}
					<Route
						path={ADMIN_PATH_ROOT + ROUTES.AdminUsers}
						element={<AdminUsersPage />}
					/>
					<Route
						path={ADMIN_PATH_ROOT + ROUTES.AdminUserCreate}
						element={<AdminUserCreatePage />}
					/>
					<Route
						path={ADMIN_PATH_ROOT + ROUTES.AdminUserDetails}
						element={<AdminUserDetailsPage />}
					/>
					<Route
						path={ADMIN_PATH_ROOT + ROUTES.AdminEnterprise}
						element={<AdminEnterprisePage />}
					/>
					{/* ENTERPRISE ADMIN */}
					{ARE_ENTERPRISE_ADMIN_ROUTES_ACTIVATED && (
						<>
							<Route
								path={
									ENTERPRISE_ADMIN_PATH_ROOT +
									ROUTES.EnterpriseAdminProjectApproval
								}
								element={<EnterpriseAdminProjectApproval />}
							/>
							<Route
								path={ENTERPRISE_ADMIN_PATH_ROOT + ROUTES.EnterpriseAdminUsers}
								element={<AccountUMSubscriptionPage />}
							/>
							<Route
								path={ENTERPRISE_ADMIN_PATH_ROOT + ROUTES.EnterpriseAdminProjects}
								element={<EnterpriseAdminProjectsPage />}
							/>
							<Route
								path={
									ENTERPRISE_ADMIN_PATH_ROOT + ROUTES.EnterpriseAdminSubscription
								}
								element={<EnterpriseAdminSubscriptionPage />}
							/>
							<Route
								path={ENTERPRISE_ADMIN_PATH_ROOT + ROUTES.EnterpriseAdminSettings}
								element={<EnterpriseAdminSettingsPage />}
							/>
						</>
					)}

					{/* TEMPLATES */}
					{IS_TEMPLATES_PAGE_ENABLED && (
						<Route path={ROUTES.Templates} element={<TemplatesPage />} />
					)}
					{/* FORMS */}
					{IS_FORMS_DESGINER_PAGE_ENABLED && (
						<>
							<Route path={ROUTES.Forms} element={<FormsPage />} />
							<Route path={ROUTES.Form} element={<FormDesignerPage />} />
						</>
					)}
				</Route>

				<Route element={<WithSubscriptionRules />}>
					{/* ACCOUNT USER MANAGEMENT*/}
					<Route path={ROUTES.AccountUMPayment} element={<AccountUMPaymentPage />} />
					<Route
						path={ROUTES.AccountUMBillingHistory}
						element={<AccountUMBillingPage />}
					/>
				</Route>

				<Route path={ROUTES.NotFoundPage} element={<NotFoundPage />} />
				<Route path={ROUTES.UnauthorizedPage} element={<UnauthorizedPage />} />
			</Routes>
		</>
	);
}
